/**
 * Chaotic Chosen Theme JavaScript
 * Handles interactive functionality, cart operations, and micro-interactions
 * Version: 2024-12-08-rate-limiting-spam-protection
 */

class ChaoticTheme {
  constructor() {
    this.cart = {
      itemCount: 0,
      items: [],
      pendingRequests: 0
    };
    
    // Security: Rate limiting for cart operations
    this.maxConcurrentRequests = 3;
    this.lastCartUpdate = 0;
    this.cartUpdateCooldown = 500; // ms
    
    this.init();
  }

  init() {
    this.initMobileMenu();
    this.initSearchDrawer();
    this.initFormHandling();
    this.initQuickAdd();
    this.initCartUpdates();
    this.initScrollAnimations();
    this.initPerformanceMonitoring();
    this.initEasterEggs();
  }

  /**
   * Mobile Menu Toggle
   */
  initMobileMenu() {
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const menuClose = document.querySelector('[data-menu-close]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');

    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        mobileMenu.removeAttribute('hidden');
        mobileMenu.setAttribute('data-open', 'true');
        document.body.style.overflow = 'hidden';
      });
    }

    if (menuClose && mobileMenu) {
      menuClose.addEventListener('click', () => {
        mobileMenu.setAttribute('data-open', 'false');
        setTimeout(() => {
          mobileMenu.setAttribute('hidden', '');
          document.body.style.overflow = '';
        }, 500);
      });
    }

    // Close on background click
    if (mobileMenu) {
      mobileMenu.addEventListener('click', (e) => {
        if (e.target === mobileMenu) {
          menuClose.click();
        }
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu && mobileMenu.getAttribute('data-open') === 'true') {
        menuClose.click();
      }
    });
  }

  /**
   * Search Drawer Toggle with improved accessibility
   */
  initSearchDrawer() {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchClose = document.querySelector('[data-search-close]');
    const searchDrawer = document.querySelector('[data-search-drawer]');
    const searchInput = document.querySelector('[data-search-input]');

    if (!searchToggle || !searchDrawer) return;

    const openSearch = () => {
      searchDrawer.removeAttribute('hidden');
      searchToggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      
      if (searchInput) {
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          searchInput.focus();
          searchInput.select();
        });
      }
    };

    const closeSearch = () => {
      searchDrawer.setAttribute('hidden', '');
      searchToggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      searchToggle.focus();
    };

    searchToggle.addEventListener('click', openSearch);
    
    if (searchClose) {
      searchClose.addEventListener('click', closeSearch);
    }

    // Close on ESC key with better error handling
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchDrawer && !searchDrawer.hasAttribute('hidden')) {
        searchClose.click();
      }
    });
  }

  /**
   * Quick Add to Cart Functionality
   */
  initQuickAdd() {
    const quickAddButtons = document.querySelectorAll('.button--quick-add');

    quickAddButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = button.dataset.variantId;
        
        if (!variantId) return;

        // Disable button and show loading state
        button.disabled = true;
        const originalText = button.textContent;
        button.textContent = 'Adding...';

        try {
          await this.addToCart(variantId, 1);
          
          // Success feedback
          button.textContent = 'Added! ✓';
          button.style.backgroundColor = 'var(--color-accent-green)';
          
          setTimeout(() => {
            button.textContent = originalText;
            button.style.backgroundColor = '';
            button.disabled = false;
          }, 2000);
        } catch (error) {
          this.logError('Quick add to cart', error);
          button.textContent = 'Error ✗';
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
        }
      });
    });
  }

  /**
   * Add item to cart
   */
  async addToCart(variantId, quantity = 1) {
    // Security: Rate limiting
    const now = Date.now();
    if (now - this.lastCartUpdate < this.cartUpdateCooldown) {
      throw new Error('Please wait before adding another item');
    }

    // Security: Concurrent request limiting
    if (this.cart.pendingRequests >= this.maxConcurrentRequests) {
      throw new Error('Too many cart operations. Please wait.');
    }

    this.cart.pendingRequests++;
    this.lastCartUpdate = now;

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': window.shopifyCSRF ? window.shopifyCSRF.token : ''
        },
        body: JSON.stringify({
          id: variantId,
          quantity: Math.min(Math.max(1, parseInt(quantity)), 99) // Limit quantity 1-99
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();
      this.updateCartCount();
      return data;
    } finally {
      this.cart.pendingRequests--;
    }
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
        
        // Trigger bounce animation
        const cartIcon = element.closest('.header__icon--cart');
        if (cartIcon) {
          cartIcon.setAttribute('data-cart-updated', 'true');
          setTimeout(() => {
            cartIcon.removeAttribute('data-cart-updated');
          }, 500);
        }
      });

      this.cart = cart;
    } catch (error) {
      this.logError('Update cart count', error);
    }
  }

  /**
   * Initialize form handling for contact forms
   */
  initFormHandling() {
    // Check if contact form was just submitted successfully
    if (window.location.search.includes('contact%5Bform_posted%5D=true') || 
        window.location.search.includes('contact[form_posted]=true')) {
      console.log('Contact form was submitted successfully!');
      this.showContactSuccess();
    }
    
    // Handle all contact forms
    const forms = document.querySelectorAll('.contact-form__form, form[action*="contact"]');
    
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        console.log('Form submission detected:', form);
        
        // Rate limiting - prevent rapid submissions (60 second cooldown)
        const lastSubmission = localStorage.getItem('lastContactSubmission');
        const now = Date.now();
        const cooldownPeriod = 60000; // 60 seconds
        
        if (lastSubmission && (now - parseInt(lastSubmission)) < cooldownPeriod) {
          const remainingTime = Math.ceil((cooldownPeriod - (now - parseInt(lastSubmission))) / 1000);
          console.log(`Rate limit active - ${remainingTime} seconds remaining`);
          e.preventDefault();
          
          // Show rate limit message
          const existingMessage = form.querySelector('.rate-limit-message');
          if (!existingMessage) {
            const rateMessage = document.createElement('div');
            rateMessage.className = 'rate-limit-message';
            rateMessage.style.cssText = `
              background: #fff3cd;
              border: 1px solid #ffeaa7;
              color: #856404;
              padding: 0.75rem;
              border-radius: 4px;
              margin: 0.5rem 0;
              text-align: center;
            `;
            rateMessage.innerHTML = `Whoa there, slow down! Wait ${remainingTime} seconds before sending another message.`;
            form.insertBefore(rateMessage, form.firstChild);
            
            // Remove message after remaining time
            setTimeout(() => {
              if (rateMessage.parentNode) {
                rateMessage.parentNode.removeChild(rateMessage);
              }
            }, remainingTime * 1000);
          }
          return false;
        }
        
        // Store submission timestamp
        localStorage.setItem('lastContactSubmission', now.toString());
        console.log('Rate limit check passed, storing submission timestamp');
        
        // Honeypot validation - smart detection that ignores browser auto-fill
        const honeypot = form.querySelector('[name="bot_field"]');
        console.log('Honeypot field found:', honeypot);
        console.log('Honeypot value:', honeypot ? honeypot.value : 'no honeypot');
        
        if (honeypot && honeypot.value && honeypot.value.trim().length > 0) {
          // Check if the value looks like it came from auto-fill (email format)
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(honeypot.value.trim())) {
            console.log('Bot detected (honeypot filled with non-email), preventing submission');
            e.preventDefault();
            return false;
          } else {
            console.log('Honeypot filled with email (likely auto-fill), allowing submission');
          }
        }
        
        console.log('Honeypot check passed, proceeding with submission');

        // Enhanced validation for required fields and spam prevention
        const requiredFields = form.querySelectorAll('[required]');
        let isValid = true;
        
        requiredFields.forEach(field => {
          if (!field.value || !field.value.trim()) {
            isValid = false;
            field.classList.add('error');
            console.log('Required field missing:', field.name);
          } else {
            field.classList.remove('error');
          }
        });
        
        // Message length validation (minimum 10 characters to prevent spam)
        const messageField = form.querySelector('[name="contact[body]"]') || form.querySelector('textarea');
        if (messageField && messageField.value.trim().length < 10) {
          isValid = false;
          messageField.classList.add('error');
          console.log('Message too short - minimum 10 characters required');
          
          // Show message length error
          const existingError = form.querySelector('.message-length-error');
          if (!existingError) {
            const lengthError = document.createElement('div');
            lengthError.className = 'message-length-error';
            lengthError.style.cssText = `
              color: #dc3545;
              font-size: 0.875rem;
              margin-top: 0.25rem;
            `;
            lengthError.textContent = 'Message must be at least 10 characters long.';
            messageField.parentNode.appendChild(lengthError);
            
            // Remove error when user starts typing
            messageField.addEventListener('input', function() {
              if (this.value.trim().length >= 10 && lengthError.parentNode) {
                lengthError.parentNode.removeChild(lengthError);
                this.classList.remove('error');
              }
            });
          }
        }

        if (!isValid) {
          console.log('Form validation failed');
          e.preventDefault();
          return false;
        }

        console.log('Form validation passed, adding loading state');

        // Add loading state
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) {
          const buttonText = submitButton.querySelector('.button__text') || submitButton;
          const originalText = buttonText.textContent;
          
          console.log('Changing button text from', originalText, 'to Sending...');
          buttonText.textContent = 'Sending...';
          submitButton.disabled = true;
          form.classList.add('loading');

          // Reset after 8 seconds and assume success (Shopify doesn't always redirect immediately)
          const self = this;
          setTimeout(() => {
            console.log('Form submission timeout reached - assuming success');
            buttonText.textContent = originalText;
            submitButton.disabled = false;
            form.classList.remove('loading');
            
            // Show success message since form likely submitted successfully
            self.showContactSuccess();
            
            console.log('Contact form assumed successful after timeout');
          }, 8000);
        }
        
        console.log('Allowing form to submit naturally to:', form.action || '/contact');
        
        // Listen for successful form submission
        const handleFormSubmission = () => {
          // Check for various success indicators
          if (window.location.search.includes('contact%5Bform_posted%5D=true') || 
              window.location.search.includes('contact[form_posted]=true') ||
              window.location.hash.includes('contact_form')) {
            console.log('Contact form submitted successfully!');
            return true;
          }
          return false;
        };
        
        // Check immediately and periodically for success
        setTimeout(handleFormSubmission, 500);
        
        // Also listen for page navigation events
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;
        
        const checkAfterNavigation = () => {
          setTimeout(() => {
            if (handleFormSubmission()) {
              // Form was successful, reset button immediately
              const buttonText = submitButton.querySelector('.button__text') || submitButton;
              buttonText.textContent = originalText;
              submitButton.disabled = false;
              form.classList.remove('loading');
            }
          }, 100);
        };
        
        history.pushState = function() {
          originalPushState.apply(history, arguments);
          checkAfterNavigation();
        };
        
        history.replaceState = function() {
          originalReplaceState.apply(history, arguments);
          checkAfterNavigation();
        };
        
        // Form will now submit naturally to Shopify
      });
    });
  }

  /**
   * Show success message for contact form submission
   */
  showContactSuccess() {
    console.log('Showing contact form success message');
    
    // Find contact form or create success message area
    const contactForm = document.querySelector('.contact-form__form-wrapper') || 
                       document.querySelector('.contact-form') ||
                       document.querySelector('main');
                       
    if (contactForm) {
      // Create success message
      const successMessage = document.createElement('div');
      successMessage.className = 'contact-success';
      successMessage.style.cssText = `
        background: #d4edda;
        border: 1px solid #c3e6cb;
        color: #155724;
        padding: 1rem;
        border-radius: 4px;
        margin: 1rem 0;
        text-align: center;
      `;
      successMessage.innerHTML = '<strong>Thanks for reaching out!</strong> We\'ll get back to you soon.';
      
      // Insert at the beginning of the contact area
      contactForm.insertBefore(successMessage, contactForm.firstChild);
      
      // Auto-remove after 8 seconds
      setTimeout(() => {
        successMessage.style.opacity = '0';
        successMessage.style.transition = 'opacity 0.5s ease';
        setTimeout(() => {
          if (successMessage.parentNode) {
            successMessage.parentNode.removeChild(successMessage);
          }
        }, 500);
      }, 8000);
    }
  }

  /**
   * Initialize cart-related updates
   */
  initCartUpdates() {
    // Update cart count on page load
    this.updateCartCount();

    // Handle cart form submissions
    const cartForms = document.querySelectorAll('form[action*="/cart"]');
    cartForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        
        try {
          const response = await fetch(form.action, {
            method: 'POST',
            headers: {
              'X-CSRF-Token': window.shopifyCSRF ? window.shopifyCSRF.token : ''
            },
            body: formData
          });

          if (response.ok) {
            this.updateCartCount();
            if (form.classList.contains('product-template__form')) {
              window.location.href = window.routes.cart_url;
            }
          }
        } catch (error) {
          this.logError('Cart form submission', error);
        }
      });
    });
    
    // Enhanced newsletter form handling
    const newsletterForms = document.querySelectorAll('.newsletter__form, .contact-form__form');
    newsletterForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        // Honeypot validation
        const honeypot = form.querySelector('[name="bot_field"]');
        if (honeypot && honeypot.value !== '') {
          e.preventDefault();
          return false;
        }

        // Add loading state
        const submitButton = form.querySelector('button[type="submit"]');
        const buttonText = submitButton.querySelector('.button__text') || submitButton;
        const originalText = buttonText.textContent;
        
        buttonText.textContent = 'Sending...';
        submitButton.disabled = true;
        form.classList.add('loading');

        // Reset after a delay if no page refresh happens (form didn't submit successfully)
        setTimeout(() => {
          buttonText.textContent = originalText;
          submitButton.disabled = false;
          form.classList.remove('loading');
        }, 5000);
      });
    });
  }

  /**
   * Error logging for debugging
   */
  logError(message, data = {}) {
    if (window.Shopify && window.Shopify.designMode) {
      console.warn(`ChaoticTheme: ${message}`, data);
    }
  }

  /**
   * Performance monitoring
   */
  initPerformanceMonitoring() {
    // Monitor Core Web Vitals in development
    if (window.Shopify && window.Shopify.designMode) {
      try {
        import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
          getCLS(console.log);
          getFID(console.log);
          getFCP(console.log);
          getLCP(console.log);
          getTTFB(console.log);
        });
      } catch (e) {
        // Web Vitals not available
      }
    }
  }

  /**
   * Scroll animations with Intersection Observer
   */
  initScrollAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    // Observe product cards and sections
    const animatedElements = document.querySelectorAll('.product-card, .collection-showcase__item, .image-with-text');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(2rem)';
      el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      observer.observe(el);
    });
  }

  /**
   * Easter eggs and fun interactions
   */
  initEasterEggs() {
    // Occasional random character peek from corner (rare)
    if (Math.random() < 0.1) { // 10% chance
      setTimeout(() => {
        this.showCornerPeek();
      }, 5000 + Math.random() * 10000);
    }

    // Konami code easter egg (up, up, down, down, left, right, left, right, b, a)
    const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    let konamiIndex = 0;

    document.addEventListener('keydown', (e) => {
      if (e.key === konamiCode[konamiIndex]) {
        konamiIndex++;
        if (konamiIndex === konamiCode.length) {
          this.activateChaosMode();
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    });
  }

  /**
   * Show a cute character peeking from corner
   */
  showCornerPeek() {
    const peek = document.createElement('div');
    peek.style.cssText = `
      position: fixed;
      bottom: -100px;
      right: 20px;
      width: 80px;
      height: 100px;
      background: var(--color-accent-pink);
      border-radius: 50% 50% 0 0;
      z-index: 9999;
      transition: bottom 0.5s ease;
      cursor: pointer;
    `;
    
    // Add simple eyes
    peek.innerHTML = `
      <div style="position: absolute; top: 20px; left: 20px; width: 10px; height: 10px; background: #000; border-radius: 50%;"></div>
      <div style="position: absolute; top: 20px; right: 20px; width: 10px; height: 10px; background: #000; border-radius: 50%;"></div>
    `;

    document.body.appendChild(peek);

    // Animate in
    setTimeout(() => {
      peek.style.bottom = '0px';
    }, 100);

    // Animate out after 3 seconds
    setTimeout(() => {
      peek.style.bottom = '-100px';
      setTimeout(() => {
        peek.remove();
      }, 500);
    }, 3000);

    // Click to dismiss
    peek.addEventListener('click', () => {
      peek.style.bottom = '-100px';
      setTimeout(() => {
        peek.remove();
      }, 500);
    });
  }

  /**
   * Chaos mode activation (fun secret)
   */
  activateChaosMode() {
    // Add rainbow gradient to buttons temporarily
    const buttons = document.querySelectorAll('.button');
    buttons.forEach(button => {
      button.style.background = 'linear-gradient(45deg, #ff006e, #8338ec, #a7ff00, #ffbe0b)';
      button.style.backgroundSize = '400% 400%';
      button.style.animation = 'chaosGradient 3s ease infinite';
    });

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes chaosGradient {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `;
    document.head.appendChild(style);

    // Show message
    const message = document.createElement('div');
    message.textContent = '✨ CHAOS MODE ACTIVATED ✨';
    message.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 2rem 4rem;
      background: var(--color-accent-pink);
      color: var(--color-base-cream);
      font-family: var(--font-heading-family);
      font-size: 2.4rem;
      font-weight: 900;
      z-index: 10000;
      animation: rumble 0.5s ease infinite;
    `;
    document.body.appendChild(message);

    setTimeout(() => {
      message.remove();
      buttons.forEach(button => {
        button.style.background = '';
        button.style.animation = '';
      });
      style.remove();
    }, 5000);
  }
  
  /**
   * Secure error logging
   * Only logs detailed errors in Shopify design mode, generic messages in production
   */
  logError(context, error) {
    if (window.Shopify && window.Shopify.designMode) {
      // Full logging in theme editor
      console.error(`[${context}]`, error);
    } else {
      // Generic message in production
      console.error(`An error occurred in ${context}`);
    }
  }
}

// Initialize theme when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.chaoticTheme = new ChaoticTheme();
});

// Collection filtering and sorting
if (document.querySelector('#SortBy')) {
  document.querySelector('#SortBy').addEventListener('change', function() {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', this.value);
    window.location.href = url.toString();
  });
}

// Filter drawer toggle
const filterToggle = document.querySelector('.collection-grid__filter-toggle');
const filterDrawer = document.querySelector('.collection-grid__filters-drawer');
const filterClose = document.querySelector('.collection-grid__filters-close');

if (filterToggle && filterDrawer) {
  filterToggle.addEventListener('click', () => {
    filterDrawer.removeAttribute('hidden');
    filterToggle.setAttribute('aria-expanded', 'true');
  });
}

if (filterClose && filterDrawer) {
  filterClose.addEventListener('click', () => {
    filterDrawer.setAttribute('hidden', '');
    if (filterToggle) {
      filterToggle.setAttribute('aria-expanded', 'false');
    }
  });
}
